function getSelectedRanges(selection: Selection): Range[] {
  return Array.from({ length: selection.rangeCount }, (_, index) => selection.getRangeAt(index).cloneRange());
}

function restoreSelection(selection: Selection, ranges: Range[]) {
  selection.removeAllRanges();
  ranges.forEach((range) => selection.addRange(range));
}

function applyCopySpacing(element: HTMLElement) {
  element.querySelectorAll<HTMLElement>('p').forEach((paragraph) => {
    if (paragraph.closest('table')) {
      paragraph.style.margin = '0';
      return;
    }

    paragraph.style.margin = '0 0 12px 0';
  });

  element.querySelectorAll<HTMLElement>('ul, ol').forEach((list) => {
    if (list.closest('table')) {
      list.style.margin = '0';
      list.style.padding = '0';
      return;
    }

    list.style.margin = '0 0 12px 24px';
    list.style.padding = '0';
  });

  element.querySelectorAll<HTMLElement>('li').forEach((listItem) => {
    if (listItem.closest('table')) {
      listItem.style.margin = '0';
      return;
    }

    listItem.style.margin = '0 0 4px 0';
  });

  element.querySelectorAll<HTMLElement>('table').forEach((table) => {
    table.style.margin = '0 0 12px 0';
  });

  element.querySelectorAll<HTMLElement>('th, td').forEach((cell) => {
    cell.style.paddingTop = '0';
    cell.style.paddingBottom = '0';
    cell.style.verticalAlign = 'top';
  });
}

function removeCopyExcludedContent(element: HTMLElement) {
  element.querySelectorAll('[data-nova-copy-exclude="true"]').forEach((node) => node.remove());
}

function createCopyElement(element: HTMLElement): HTMLElement {
  const copyElement = element.cloneNode(true) as HTMLElement;
  copyElement.style.position = 'fixed';
  copyElement.style.left = '-9999px';
  copyElement.style.top = '0';
  copyElement.style.width = `${Math.max(element.offsetWidth, 1)}px`;
  copyElement.style.pointerEvents = 'none';
  copyElement.setAttribute('aria-hidden', 'true');
  removeCopyExcludedContent(copyElement);
  applyCopySpacing(copyElement);
  document.body.append(copyElement);

  return copyElement;
}

export function copyElementToClipboard(element: HTMLElement | null): boolean {
  if (!element?.textContent?.trim()) {
    return false;
  }

  const selection = window.getSelection();
  if (!selection) {
    return false;
  }

  if (typeof document.execCommand !== 'function') {
    return false;
  }

  const previousRanges = getSelectedRanges(selection);
  const copyElement = createCopyElement(element);
  const range = document.createRange();
  range.selectNodeContents(copyElement);

  try {
    selection.removeAllRanges();
    selection.addRange(range);
    return document.execCommand('copy');
  } finally {
    restoreSelection(selection, previousRanges);
    copyElement.remove();
  }
}

function copyTextWithExecCommand(text: string): boolean {
  if (typeof document === 'undefined' || typeof document.execCommand !== 'function') {
    return false;
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  textarea.style.top = '0';
  textarea.style.opacity = '0';
  document.body.append(textarea);

  const selection = typeof window !== 'undefined' ? window.getSelection() : null;
  const previousRanges = selection ? getSelectedRanges(selection) : [];

  try {
    textarea.focus();
    textarea.select();
    textarea.setSelectionRange(0, textarea.value.length);
    return document.execCommand('copy');
  } finally {
    if (selection) {
      restoreSelection(selection, previousRanges);
    }
    textarea.remove();
  }
}

export async function writeTextToClipboard(text: string): Promise<void> {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch {
      // Fall back to the selection-based copy path below for browser contexts
      // where the Clipboard API exists but rejects.
    }
  }

  if (copyTextWithExecCommand(text)) {
    return;
  }

  throw new Error('Unable to write text to clipboard');
}
